// src/logic/helpers/telegram.ts
import { TELEGRAM_MINI_APP_URL } from "../../state/config";

/** Predefined share payload used while testing the inbox button. */
export const TEST_INBOX_SHARE = {
    url: TELEGRAM_MINI_APP_URL,
    text: "Join me on Pebbler — let's spin the slot machine together!",
} as const;

/**
 * Returns the Telegram WebApp bridge object, or null when the app is
 * opened outside of Telegram (e.g. plain browser during development).
 */
export function getTelegramWebApp(): TelegramWebApp | null {
    return window.Telegram?.WebApp ?? null;
}

/**
 * True when running inside a real Telegram client. The script in
 * index.html always defines `window.Telegram.WebApp`, so we check for
 * actual init data instead.
 */
export function isTelegramEnv(): boolean {
    const webApp = getTelegramWebApp();
    return !!webApp && webApp.initData.length > 0;
}

const TELEGRAM_MOBILE_PLATFORMS: ReadonlySet<string> = new Set([
    "ios",
    "android",
    "android_x",
]);

/**
 * True for Telegram's phone clients. Desktop (`tdesktop`, `macos`) and web
 * (`weba`, `webk`, `web`) clients report other platform values.
 */
export function isTelegramMobilePlatform(webApp: TelegramWebApp): boolean {
    return TELEGRAM_MOBILE_PLATFORMS.has(webApp.platform);
}

/**
 * Opens Telegram's native share dialog so the user can forward a URL and
 * optional message to another chat. Uses the `t.me/share/url` deep link
 * via `WebApp.openTelegramLink` (see Telegram Mini App docs).
 */
export function shareUrlViaTelegram(url: string, text?: string): boolean {
    const webApp = getTelegramWebApp();
    if (!webApp || typeof webApp.openTelegramLink !== "function") return false;

    const encodedUrl = encodeURIComponent(url);
    const encodedText = text ? `&text=${encodeURIComponent(text)}` : "";
    webApp.openTelegramLink(
        `https://t.me/share/url?url=${encodedUrl}${encodedText}`,
    );
    return true;
}

export type TelegramInvoiceStatus = "paid" | "cancelled" | "failed" | "pending";

/** Opens Telegram's native invoice sheet and resolves with its terminal state. */
export function openTelegramInvoice(
    invoiceLink: string,
    onOpened?: () => void,
): Promise<TelegramInvoiceStatus> {
    const webApp = getTelegramWebApp();
    if (!webApp || typeof webApp.openInvoice !== "function") {
        return Promise.reject(
            new Error("Telegram invoice checkout is unavailable"),
        );
    }

    return new Promise((resolve, reject) => {
        try {
            webApp.openInvoice(invoiceLink, resolve);
            onOpened?.();
        } catch (error) {
            reject(error);
        }
    });
}
